import { useState, useEffect, useCallback } from "react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MapPin, Search, Loader2, Sparkles, Building, TrendingUp, Zap, Camera, Eye, Download } from "lucide-react";
import { toast } from "sonner";
import { IndicativeDisclaimer } from "@/components/IndicativeDisclaimer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

export const Prefeasibility = () => {
  const [loading, setLoading] = useState(false);
  const [quickLoading, setQuickLoading] = useState(false);
  const [extractingRoad, setExtractingRoad] = useState(false);
  const [roadAnalysis, setRoadAnalysis] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [formData, setFormData] = useState({
    location_lat: "19.0760",
    location_lng: "72.8777",
    plot_area: "",
    current_fsi: "",
    building_age: "",
    road_width: ""
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setFormData({
            ...formData,
            location_lat: position.coords.latitude.toFixed(6),
            location_lng: position.coords.longitude.toFixed(6)
          });
          toast.success("Location detected");
        },
        () => {
          toast.error("Unable to get location");
        }
      );
    }
  };

  const extractRoadWidth = async () => {
    if (!formData.location_lat || !formData.location_lng) {
      toast.error("Please enter location coordinates first");
      return;
    }

    setExtractingRoad(true);
    setRoadAnalysis(null);
    toast.info("Analyzing Street View for road width...", { duration: 3000 });

    try {
      const res = await fetch(`${API}/extract-road-width`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          location_lat: parseFloat(formData.location_lat),
          location_lng: parseFloat(formData.location_lng)
        }),
        credentials: "include"
      });

      if (res.ok) {
        const data = await res.json();
        setRoadAnalysis(data);
        
        // Auto-fill road width
        if (data.analysis?.estimated_width_meters) {
          setFormData(prev => ({
            ...prev,
            road_width: data.analysis.estimated_width_meters.toString()
          }));
          toast.success(`Road width estimated: ${data.analysis.estimated_width_meters}m`);
        }
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to extract road width");
      }
    } catch (error) {
      toast.error("Failed to extract road width");
    } finally {
      setExtractingRoad(false);
    }
  };

  const handleAnalyze = async (e, quick = false) => {
    e.preventDefault();
    
    if (!formData.location_lat || !formData.location_lng) {
      toast.error("Please enter location coordinates");
      return;
    }

    if (quick) {
      setQuickLoading(true);
    } else {
      setLoading(true);
      toast.info("AI is analyzing location. This may take 15-30 seconds...", { duration: 5000 });
    }
    
    try {
      const endpoint = quick ? `${API}/prefeasibility/quick` : `${API}/prefeasibility`;
      const payload = quick 
        ? {
            location_lat: parseFloat(formData.location_lat),
            location_lng: parseFloat(formData.location_lng),
            location_address: null,
            road_width: formData.road_width ? parseFloat(formData.road_width) : null
          }
        : {
            location_lat: parseFloat(formData.location_lat),
            location_lng: parseFloat(formData.location_lng),
            plot_area: formData.plot_area ? parseFloat(formData.plot_area) : null,
            current_fsi: formData.current_fsi ? parseFloat(formData.current_fsi) : null,
            building_age: formData.building_age ? parseInt(formData.building_age) : null
          };
      
      const res = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        credentials: "include"
      });

      if (res.ok) {
        const data = await res.json();
        setAnalysis(data);
        toast.success(quick ? "Quick analysis complete!" : "Pre-feasibility analysis complete");
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to analyze location");
      }
    } catch (error) {
      toast.error("Failed to analyze location");
    } finally {
      setLoading(false);
      setQuickLoading(false);
    }
  };

  const [downloading, setDownloading] = useState(false);
  const [requestingExpert, setRequestingExpert] = useState(false);

  const requestExpertReview = async () => {
    const phone = window.prompt("Leave your phone number and our expert will call you back for a professional review:");
    if (phone === null) return;
    setRequestingExpert(true);
    try {
      const res = await fetch(`${API}/leads/expert-review`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          phone,
          note: "Requested expert review from Pre-feasibility page",
          context: analysis ? `Zone: ${analysis.zone}, Regulation: ${analysis.regulation}, FSI: ${analysis.effective_fsi}, Loc: ${analysis.lat},${analysis.lng}` : "No analysis run yet"
        }),
        credentials: "include"
      });
      const data = await res.json();
      if (res.ok) toast.success(data.message || "Our team will reach out to you shortly.");
      else toast.error(data.detail || "Failed to submit request");
    } catch {
      toast.error("Failed to submit request");
    } finally {
      setRequestingExpert(false);
    }
  };

  const downloadPDF = async () => {
    if (!analysis?.analysis) return;
    setDownloading(true);
    try {
      const res = await fetch(`${API}/reports/pdf`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title: `Pre-feasibility Report${analysis.quick_mode ? " (Quick Check)" : ""}`,
          subtitle: `Location: ${analysis.lat}, ${analysis.lng}`,
          report_text: analysis.analysis,
          meta: {
            "Zone": analysis.zone,
            "Governing Regulation": analysis.regulation,
            "Effective FSI": analysis.effective_fsi,
            "Road Width (m)": formData.road_width,
            "Plot Area (sq.m)": formData.plot_area,
            "Viability": analysis.is_viable != null ? (analysis.is_viable ? "Viable" : "Needs detailed assessment") : null
          }
        }),
        credentials: "include"
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "Prefeasibility_Report.pdf";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch {
      toast.error("Failed to download PDF");
    } finally {
      setDownloading(false);
    }
  };

  const formatAnalysis = (text) => {
    if (!text) return null;
    
    // Clean up markdown formatting (remove *, **, #, ##, etc.)
    const cleanText = text
      .replace(/\*\*([^*]+)\*\*/g, '$1')  // Remove bold **text**
      .replace(/\*([^*]+)\*/g, '$1')       // Remove italic *text*
      .replace(/^#{1,6}\s*/gm, '')         // Remove # headings
      .replace(/`([^`]+)`/g, '$1')         // Remove inline code
      .replace(/^\s*[-*]\s*/gm, '• ')      // Convert - or * bullets to •
      .trim();
    
    const sections = cleanText.split(/\n\n+/);
    
    return sections.map((section, idx) => {
      // Check if it looks like a heading (numbered or ends with colon)
      if (section.match(/^\d+\.\s+[A-Z]/) || (section.length < 80 && section.endsWith(':'))) {
        return (
          <h3 key={idx} className="text-lg font-semibold text-slate-800 mt-4 mb-2" style={{ fontFamily: 'Manrope' }}>
            {section}
          </h3>
        );
      }
      
      // Check for bullet points
      if (section.includes('\n•') || section.startsWith('•')) {
        const items = section.split('\n').filter(Boolean);
        return (
          <ul key={idx} className="space-y-1 text-slate-700 mb-4">
            {items.map((item, i) => (
              <li key={i} className="leading-relaxed flex items-start gap-2">
                <span className="text-emerald-600 mt-1">•</span>
                <span>{item.replace(/^•\s*/, '')}</span>
              </li>
            ))}
          </ul>
        );
      }
      
      return (
        <p key={idx} className="text-slate-700 leading-relaxed mb-4">
          {section}
        </p>
      );
    });
  };

  const mapUrl = `https://www.google.com/maps/embed/v1/place?key=${GOOGLE_MAPS_API_KEY}&q=${formData.location_lat},${formData.location_lng}&zoom=15`;

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
            Location Pre-feasibility
          </h1>
          <p className="text-slate-600">Analyze redevelopment potential based on location</p>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Left Side - Map and Form */}
          <div className="space-y-6">
            {/* Map */}
            <Card>
              <CardContent className="p-0">
                <div className="map-container">
                  <iframe
                    title="Location Map"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    loading="lazy"
                    allowFullScreen
                    referrerPolicy="no-referrer-when-downgrade"
                    src={mapUrl}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Form */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-emerald-700" />
                  Location Details
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleAnalyze} className="space-y-4">
                  <div className="flex gap-2">
                    <Button 
                      type="button" 
                      variant="outline"
                      onClick={getCurrentLocation}
                      className="flex-1"
                      data-testid="detect-location-btn"
                    >
                      <MapPin className="w-4 h-4 mr-2" />
                      Use Current Location
                    </Button>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="location_lat">Latitude *</Label>
                      <Input
                        id="location_lat"
                        name="location_lat"
                        value={formData.location_lat}
                        onChange={handleChange}
                        placeholder="19.0760"
                        data-testid="lat-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="location_lng">Longitude *</Label>
                      <Input
                        id="location_lng"
                        name="location_lng"
                        value={formData.location_lng}
                        onChange={handleChange}
                        placeholder="72.8777"
                        data-testid="lng-input"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="plot_area">Plot Area (sq.m)</Label>
                      <Input
                        id="plot_area"
                        name="plot_area"
                        type="number"
                        value={formData.plot_area}
                        onChange={handleChange}
                        placeholder="2500"
                        data-testid="plot-area-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="road_width">Road Width (m)</Label>
                      <div className="flex gap-2">
                        <Input
                          id="road_width"
                          name="road_width"
                          type="number"
                          value={formData.road_width}
                          onChange={handleChange}
                          placeholder="12"
                          className="flex-1"
                          data-testid="road-width-input"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={extractRoadWidth}
                          disabled={extractingRoad}
                          title="Auto-detect from Street View"
                          data-testid="extract-road-btn"
                        >
                          {extractingRoad ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Camera className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Road Width Analysis Result */}
                  {roadAnalysis && (
                    <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                      <div className="flex items-start gap-2">
                        <Eye className="w-4 h-4 text-blue-600 mt-0.5" />
                        <div className="text-sm">
                          <p className="font-medium text-blue-800">
                            AI Road Analysis: ~{roadAnalysis.analysis?.estimated_width_meters}m
                            <span className="ml-2 text-xs px-2 py-0.5 bg-blue-200 rounded">
                              {roadAnalysis.analysis?.confidence} confidence
                            </span>
                          </p>
                          <p className="text-blue-700 text-xs mt-1">
                            {roadAnalysis.analysis?.lanes} lanes • {roadAnalysis.analysis?.road_type} • 
                            FSI eligibility: {roadAnalysis.fsi_by_road_width}
                          </p>
                          {roadAnalysis.analysis?.observations && (
                            <p className="text-blue-600 text-xs mt-1 italic">
                              {roadAnalysis.analysis.observations}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="current_fsi">Current FSI</Label>
                      <Input
                        id="current_fsi"
                        name="current_fsi"
                        type="number"
                        step="0.1"
                        value={formData.current_fsi}
                        onChange={handleChange}
                        placeholder="1.0"
                        data-testid="fsi-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="building_age">Building Age</Label>
                      <Input
                        id="building_age"
                        name="building_age"
                        type="number"
                        value={formData.building_age}
                        onChange={handleChange}
                        placeholder="30"
                        data-testid="age-input"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <Button 
                      type="button"
                      variant="outline"
                      className="border-emerald-200 hover:bg-emerald-50"
                      onClick={(e) => handleAnalyze(e, true)}
                      disabled={loading || quickLoading}
                      data-testid="quick-analyze-btn"
                    >
                      {quickLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Analyzing...
                        </>
                      ) : (
                        <>
                          <Zap className="w-4 h-4 mr-2" />
                          Quick Check
                        </>
                      )}
                    </Button>
                    <Button 
                      type="submit" 
                      className="bg-emerald-700 hover:bg-emerald-800"
                      disabled={loading || quickLoading}
                      data-testid="analyze-btn"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          AI Analyzing...
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4 mr-2" />
                          Full AI Analysis
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Right Side - Analysis Results */}
          <div>
            {!analysis ? (
              <Card className="h-full border-dashed">
                <CardContent className="h-full flex flex-col items-center justify-center py-16">
                  <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mb-6">
                    <Building className="w-8 h-8 text-emerald-700" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>
                    Enter Location Details
                  </h3>
                  <p className="text-slate-600 text-center max-w-sm mb-4">
                    Get pre-feasibility analysis including FSI entitlements, market rates, and redevelopment potential.
                  </p>
                  <div className="text-sm text-slate-500 space-y-1">
                    <p><Zap className="w-3 h-3 inline mr-1" /> <strong>Quick Check</strong>: Instant FSI calculation</p>
                    <p><Sparkles className="w-3 h-3 inline mr-1" /> <strong>Full AI</strong>: Detailed market analysis</p>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardHeader>
                  <div className="flex items-start justify-between gap-2">
                    <CardTitle className="flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-emerald-700" />
                      Pre-feasibility Analysis
                      {analysis.quick_mode && (
                        <span className="ml-2 bg-blue-100 text-blue-700 px-2 py-0.5 rounded text-xs">
                          Quick Mode
                        </span>
                      )}
                    </CardTitle>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={downloadPDF}
                      disabled={downloading}
                      data-testid="download-prefeasibility-pdf-btn"
                    >
                      {downloading ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Download className="w-4 h-4 mr-2" />
                      )}
                      PDF
                    </Button>
                  </div>
                  <div className="flex items-center gap-4 mt-2 flex-wrap">
                    <span className="bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full text-sm font-medium">
                      Zone: {analysis.zone}
                    </span>
                    {analysis.regulation && (
                      <span className="bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-sm font-medium" data-testid="regulation-badge">
                        {analysis.regulation}
                      </span>
                    )}
                    {analysis.effective_fsi && (
                      <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm font-medium">
                        FSI: {analysis.effective_fsi} (indicative)
                      </span>
                    )}
                    <span className="text-sm text-slate-500">
                      {analysis.lat}, {analysis.lng}
                    </span>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="markdown-content" data-testid="prefeasibility-analysis">
                    {formatAnalysis(analysis.analysis)}
                  </div>
                  
                  {/* Indicative-only disclaimer */}
                  <IndicativeDisclaimer className="mt-6" />

                  {/* Lead-gen: human expert review upsell */}
                  <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-3 p-4 rounded-lg bg-emerald-50 border border-emerald-200">
                    <p className="text-sm text-emerald-800">
                      Want a professional, human-reviewed feasibility report for your society?
                    </p>
                    <Button
                      onClick={requestExpertReview}
                      disabled={requestingExpert}
                      className="bg-emerald-700 hover:bg-emerald-800 flex-shrink-0"
                      data-testid="request-expert-review-btn"
                    >
                      {requestingExpert ? "Submitting..." : "Request Expert Review"}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};
